/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/**
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/utils/types').ExtractionResult} ExtractionResult
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').Field} Field
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').Table} Table
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').ExtractionField} ExtractionField
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').StructuredExtractionResults} StructuredExtractionResults
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').TestDataSummary} TestDataSummary
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').TestSummaryResults} TestSummaryResults
 * @typedef {import('../idp-extraction-project/structured-extraction/extracted-results/types').TestSummaryDetailedResults} TestSummaryDetailedResults
 */

/**
 * @param {Array<Array<unknown>> | unknown} value
 * @returns {boolean}
 */
export function isMatrix(value) {
  return Array.isArray(value) && value.length > 0 && value.every(Array.isArray);
}

/**
 * @param {ExtractionField} entry
 * @returns {Field}
 */
export function createField({ id, name, replacedName, expectedValue, confidence, originalKey, active = false }) {
  return {
    value: expectedValue,
    replacedName: replacedName ?? name,
    fieldName: originalKey ?? name,
    confidence,
    name,
    active,
    id
  };
}

/**
 * @param {Array<Array<number>> | number | string} confidence
 * @returns {string}
 */
export function getConfidenceString(confidence) {
  const parsed = parseIfJson(confidence);

  if (isMatrix(parsed)) {
    return JSON.stringify(parsed.map((row) => row.map((cell) => Math.round(cell * 100))));
  }

  // at this point is either a number or a string
  const number = parseFloat(parsed);

  if (!isNaN(number)) {
    return Math.round(number * 100).toString();
  }

  return '';
}

/**
 * @param {Array<Array<string>> | string} value
 * @returns {string}
 */
export function getValueString(value) {
  if (value == null || value === '') {
    return '';
  }
  if (!value?.length) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * @param {ExtractionField} entry
 * @returns {Table}
 */
export function createTable({ id, name, replacedName, expectedValue, originalKey, confidence, active = false }) {
  return {
    confidence: parseIfJson(confidence),
    value: mapRowsWithIds(parseIfJson(expectedValue)),
    replacedName: replacedName ?? name,
    fieldName: originalKey ?? name,
    name,
    id,
    active
  };
}

/**
 * @param {unknown} value
 * @returns {unknown|Object}
 */
export function parseIfJson(value) {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : value;
  } catch {
    return value;
  }
}

/**
 * @param {Array<Array<string>>} value
 * @returns {Array<{ rowId: string, cells: Array<{ value: string, cellId: string }> }>}
 */
export const mapRowsWithIds = (value) => {
  return value?.map((row) => {
    const rowId = crypto.randomUUID();
    const cells = row.map((cell) => ({
      value: cell,
      cellId: crypto.randomUUID()
    }));
    return { rowId, cells };
  });
};

/**
 * @param {Array<Field|Table>} list
 * @param {Map<string, Field|Table>} changedMap
 * @returns {Array<Field|Table>}
 */
export function mergeChanged(list, changedMap) {
  return list.map((item) => ({ ...item, ...(changedMap.get(item.id) ?? {}) }));
}

/**
 * @param {Array<Field|Table>} list
 * @param {Map<string, Field|Table>} changedMap
 * @returns {Array<ExtractionField>}
 */
export function selectChanged(list, changedMap) {
  return list
    .map((item) => {
      const changed = changedMap.get(item.id);
      if (!changed) {
        return null;
      }
      const keys = Object.keys(changed);
      const isChanged = keys.some((key) => changed[key] !== item[key]);
      if (!isChanged) {
        return null;
      }
      return toApiExtractionField({ ...item, ...changed });
    })
    .filter(Boolean);
}

/**
 * Factory for managing entity state (fields/tables)
 * @param {Array<Field|Table>} data
 * @param {Map<string, Field|Table>} changed
 * @param {(map: Map<string, Field|Table>) => void} setChanged
 * @returns {{
 *   data: Array<Field|Table>,
 *   changed: Map<string, Field|Table>,
 *   setChanged: (map: Map<string, Field|Table>) => void,
 *   getMerged: () => Array<Field|Table>,
 *   getChanged: () => Array<ExtractionField>,
 *   getSelectedCount: () => number,
 *   getTotal: () => number
 * }}
 */
export function createEntityManager(data, changed, setChanged) {
  return {
    data,
    changed,
    setChanged,
    getMerged() {
      return mergeChanged(this.data, this.changed);
    },
    getChanged() {
      return selectChanged(this.data, this.changed);
    },
    getSelectedCount() {
      return this.getMerged().filter((e) => e.active).length;
    },
    getTotal() {
      return this.getMerged().length;
    }
  };
}

/**
 * Extracts fields from structured extraction results.
 * Only includes fields that have actual extracted values.
 * @param {ExtractionResult} result
 * @returns {Array}
 */
export function getFieldsFromResults(result) {
  if (!result?.variables) {
    return [];
  }

  return Object.keys(result.variables)
    .filter((name) => hasValidExtractedValue(result.variables[name]))
    .map((name) => ({
      name,
      confidence: getConfidenceString(result.confidenceScore?.[name]),
      active: false,
      originalKey: result.originalKeys?.[name]
    }));
}

/**
 * Extracts expected values from structured extraction results.
 * @param {Array<ExtractionField>} fields
 * @param {ExtractionResult} results
 * @returns {Array<{ id: string, expectedValue: string }>}
 */
export function getExpectedValues(fields, results) {
  if (!fields?.length || !results || !results.variables) {
    return [];
  }
  const nameToId = new Map((fields ?? []).map((f) => [f.name, f.id]));
  return fields
    .map((field) => {
      return {
        id: nameToId.get(field.name),
        expectedValue: getValueString(results.variables?.[field.replacedName ?? field.name])
      };
    })
    .filter((item) => item.expectedValue);
}

/**
 * * Normalizes structured extraction results into fields and tables.
 * @param {Array<ExtractionField>} results
 * @returns {import('stores/StructuredExtractionStore').StructuredExtractionResults}
 */
export function getNormalizedResults(results) {
  return results.reduce(
    (acc, entry) => {
      const parsed = parseIfJson(entry.expectedValue);
      if (isMatrix(parsed)) {
        acc.tables.push(createTable(entry));
      } else {
        acc.fields.push(createField(entry));
      }
      return acc;
    },
    { fields: [], tables: [] }
  );
}

/**
 * @param {Field|Table} entry
 * @returns {ExtractionField}
 */
export function toApiExtractionField(entry) {
  const confidenceValue = entry.confidence ?? '';
  const confidence = isMatrix(confidenceValue) ? JSON.stringify(confidenceValue) : String(confidenceValue);

  return {
    id: entry.id,
    name: entry.name,
    replacedName: entry.replacedName ?? entry.name,
    active: entry.active,
    confidence
  };
}

/**
 * Returns a new StructuredExtractionResults with the updated field or table, updating only allowed properties for each type.
 * @param {StructuredExtractionResults} results
 * @param {ExtractionField} updatedField
 * @returns {StructuredExtractionResults}
 */
export function getNormalizedUpdateResults(results, updatedField) {
  const updateMap = (item) =>
    item.id === id
      ? {
          ...item,
          name: updatedField.name ?? item.name,
          replacedName: updatedField.replacedName ?? item.replacedName,
          active: updatedField.active ?? item.active
        }
      : item;

  const { id } = updatedField;
  return {
    fields: results.fields?.map(updateMap),
    tables: results.tables?.map(updateMap)
  };
}

/**
 * Returns the included field/table keys for a given list of included field/table ids.
 * @param {Array<string>} includedIds
 * @param {StructuredExtractionResults} results
 * @returns {Array<string>}
 */
export function getIncludedFieldsKeys(includedIds, results) {
  if (!Array.isArray(includedIds) || !results) {
    return [];
  }
  return includedIds
    .map((id) => {
      const field = results.fields?.find((f) => f.id === id);
      const table = results.tables?.find((t) => t.id === id);
      if (field) {
        return field.fieldName;
      }
      if (table) {
        return table.fieldName;
      }
      return null;
    })
    .filter(Boolean);
}

/**
 * Returns a mapping of original field/table keys to their renamed values for a given list of included field/table ids.
 * @param {Array<string>} includedIds
 * @param {StructuredExtractionResults} results
 * @returns {Object}
 */
export function getRenameMappings(includedIds, results) {
  if (!Array.isArray(includedIds) || !results) {
    return {};
  }
  return includedIds.reduce((acc, id) => {
    const field = results.fields?.find((f) => f.id === id);
    const table = results.tables?.find((t) => t.id === id);
    if (field) {
      acc[field.fieldName] = field.replacedName;
    }
    if (table) {
      acc[table.fieldName] = table.replacedName;
    }
    return acc;
  }, {});
}

/**
 * Returns all included (active) field/table ids from StructuredExtractionResults.
 * @param {StructuredExtractionResults} results
 * @returns {Array<string>}
 */
export function getIncludedFieldsIds(results) {
  if (!results) {
    return [];
  }
  const fieldIds = results.fields ? results.fields.filter((f) => f.active).map((f) => f.id) : [];
  const tableIds = results.tables ? results.tables.filter((t) => t.active).map((t) => t.id) : [];
  return [...fieldIds, ...tableIds];
}

/**
 * Extracts all confidence values from a list of fields or tables.
 * @param {Array<Field|Table>} items
 * @returns {number[]}
 */
function extractConfidences(items) {
  if (!items?.length) {
    return [];
  }
  return items.flatMap((field) => {
    if (!field) {
      return [];
    }
    if (isMatrix(field.confidence)) {
      return (
        field.confidence
          // @ts-ignore
          .flat()
          .map(Number)
          .filter((v) => !Number.isNaN(v))
      );
    }
    const v = Number(field.confidence);
    return Number.isNaN(v) ? [] : [v];
  });
}

/**
 * Returns detailed summary for a single test template item.
 * @param {{ totalFields: number, totalTables: number }} params
 * @returns {function(StructuredExtractionResults): TestSummaryDetailedResults}
 */
function getDetailedResult({ totalFields, totalTables }) {
  return (item) => {
    const fieldConf = extractConfidences(item.fields);
    const tableConf = extractConfidences(item.tables);
    const all = [...fieldConf, ...tableConf];
    return {
      averageConfidence: all.length ? Math.round(all.reduce((a, b) => a + b, 0) / all.length) : 0,
      fieldsExtracted: item.fields?.length ?? 0,
      tablesExtracted: item.tables?.length ?? 0,
      extractionStatus:
        item.fields?.length === totalFields && item.tables?.length === totalTables ? 'success' : 'missing',
      fields: item.fields || [],
      tables: item.tables || []
    };
  };
}

/**
 * Returns summary results for all test template data.
 * @param {Array<StructuredExtractionResults>} testTemplateData
 * @param {{ totalFields: number, totalTables: number }} params
 * @returns {TestSummaryResults}
 */
function getSummaryResults(testTemplateData, { totalFields, totalTables }) {
  if (!Array.isArray(testTemplateData) || testTemplateData.length === 0) {
    return null;
  }
  const allFieldConfidences = testTemplateData.flatMap((item) => extractConfidences(item.fields));
  const allTableConfidences = testTemplateData.flatMap((item) => extractConfidences(item.tables));
  const allConfidences = [...allFieldConfidences, ...allTableConfidences];
  const totalConfidence = allConfidences.reduce((sum, v) => sum + v, 0);

  const allFields = testTemplateData.reduce((sum, item) => sum + (item.fields?.length || 0), 0);
  const allTables = testTemplateData.reduce((sum, item) => sum + (item.tables?.length || 0), 0);

  return {
    averageConfidence: Math.round(totalConfidence / allConfidences.length).toString(),
    averageNumberOfFieldsExtracted: Math.round(allFields / testTemplateData.length),
    averageNumberOfTablesExtracted: Math.round(allTables / testTemplateData.length),
    totalNumberOfFieldsExtracted: totalFields,
    totalNumberOfTablesExtracted: totalTables
  };
}

/**
 * Returns summary and detailed results for test template data.
 * @param {Array<StructuredExtractionResults>} testTemplateData
 * @param {{ totalFields: number, totalTables: number }} params
 * @returns {TestDataSummary|null}
 */
export function getTestSummaryResults(testTemplateData, { totalFields, totalTables }) {
  if (!testTemplateData?.length) {
    return null;
  }
  return {
    testSummaryResults: getSummaryResults(testTemplateData, { totalFields, totalTables }),
    testSummaryDetailedResults: testTemplateData.map(getDetailedResult({ totalFields, totalTables }))
  };
}

/**
 * Returns a list of active field and table ids from the structured extraction results.
 * @param {StructuredExtractionResults} results
 * @returns {{ totalActiveFields: number, totalActiveTables: number }}
 */
export const getTotalActiveFieldsAndTables = (results) => {
  if (!results) {
    return { totalActiveFields: 0, totalActiveTables: 0 };
  }

  const getActiveCount = (items) => items.filter((item) => item.active).length;

  return {
    totalActiveFields: getActiveCount(results.fields),
    totalActiveTables: getActiveCount(results.tables)
  };
};

/**
 * Checks if a value is a valid extracted value (not null, not empty, has content)
 * @param {unknown} value
 * @returns {boolean}
 */
export function hasValidExtractedValue(value) {
  if (value == null || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

/**
 * Filters persisted fields to only include active fields with valid extracted values.
 * This ensures consistency between extraction and loading flows.
 * @param {Array<ExtractionField>} persistedFields
 * @returns {Array<ExtractionField>}
 */
export function filterValidActiveFields(persistedFields) {
  if (!persistedFields) {
    return [];
  }
  return persistedFields.filter((field) => {
    return field.active && hasValidExtractedValue(field.expectedValue);
  });
}
